import type { SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/material';
import {
  MOTION_DURATION_MS,
  MOTION_EASING,
  MOTION_STAGGER_MS,
  motionTransition,
  withReducedMotion,
} from './motion';

const STAGGERED_CARD_LIMIT = 8;

export const clickableCardSx = {
  p: 2,
  borderRadius: 3,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  transition: motionTransition(['box-shadow']),
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
} as const;

export const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Slow ambient drift for the auth hero aurora background. Runs on a ::before
// pseudo larger than the viewport so the small translate never reveals an edge.
// Deliberately gentle (long duration, tiny amplitude) so it reads as "alive",
// not "moving" — the auth page is the only identity surface, brand is "calm".
// Consumers must guard it with `withReducedMotion` / `REDUCED_MOTION_QUERY`.
export const auroraDrift = keyframes`
  0% {
    transform: translate3d(0%, 0%, 0) scale(1);
  }
  50% {
    transform: translate3d(-2%, -1.5%, 0) scale(1.03);
  }
  100% {
    transform: translate3d(1.5%, 1%, 0) scale(1.02);
  }
`;

export function getCardAnimationSx(index: number): SxProps<Theme> {
  const animationSx =
    index < STAGGERED_CARD_LIMIT
      ? {
          animation: `${fadeSlideUp} ${MOTION_DURATION_MS.layout}ms ${MOTION_EASING.arrive} both`,
          animationDelay: `${MOTION_STAGGER_MS * index}ms`,
        }
      : {
          animation: `${fadeSlideUp} ${MOTION_DURATION_MS.routine}ms ${MOTION_EASING.arrive} both`,
        };
  return withReducedMotion(animationSx);
}

export const gradientDividerSx = {
  height: '1px',
  background:
    'linear-gradient(to right, transparent, rgba(0,0,0,0.06) 15%, rgba(0,0,0,0.06) 85%, transparent)',
} as const;
